import { createSignal, createEffect, createMemo } from "solid-js";
import { createInfiniteScroll } from "../../components/pagination";
import moviesCategory from "../../api/providers/moviesCategory";
import livetvChannels from "../../api/providers/livetvChannels";
import moviesProvider from "../../api/providers/movies";
import { cache, CacheKeys, CacheTTL } from "../cacheManager";
import { playListPassword, playListUrl, playListUsername } from "../../../state";

// Data for the "Live TV Channels", "Popular Movies" and "Services" rows on
// the hotel home: the first 10 channels by number, 10 random movies from the
// first real movie category, and all services. Channels and movies use the
// same cache keys as the residential home, so whichever home loads first
// fills the cache for the other.

const SERVICES_CACHE_KEY = "home_services";
const SERVICES_TTL = 15 * 60 * 1000;

// The backend can answer with an error object (e.g. {"error": "Internal
// server error"}) instead of a list. createInfiniteScroll ignores anything
// that isn't an array, so the rows would show "Loading..." forever; an empty
// list instead marks the provider as ended, and the rows show "No ... available".
const asList = (fetcher: (page: number) => Promise<any>) => (page: number) =>
  fetcher(page)
    .then((res) => {
      if (Array.isArray(res)) return res;
      console.warn("🏨 Hotel home: expected a list, got", res);
      return [];
    })
    .catch((err) => {
      console.warn("🏨 Hotel home: request failed", err);
      return [];
    });

export function useHomeRows() {
  const cachedChannels = cache.get(CacheKeys.channels("all"));
  const cachedMovies = cache.get(CacheKeys.homeMovies());

  const [channels, setChannels] = createSignal<any[]>(Array.isArray(cachedChannels) ? cachedChannels : []);
  const [movies, setMovies] = createSignal<any[]>(Array.isArray(cachedMovies) ? cachedMovies : []);
  const [isLoadingChannels, setIsLoadingChannels] = createSignal(channels().length === 0);
  const [isLoadingMovies, setIsLoadingMovies] = createSignal(movies().length === 0);
  const [movieCategoryId, setMovieCategoryId] = createSignal<string | undefined>();

  // ── Channels ──
  if (channels().length === 0) {
    const provider = createInfiniteScroll(asList(livetvChannels("all")));
    createEffect(() => {
      const pages = provider.pages();
      if (pages.length > 0) {
        const sorted = [...pages].sort((a: any, b: any) => (a.num || 0) - (b.num || 0));
        const slice = sorted.slice(0, 10);
        cache.set(CacheKeys.channels("all"), slice, CacheTTL.channels);
        setChannels(slice);
        setIsLoadingChannels(false);
      } else if (provider.end()) {
        setIsLoadingChannels(false);
      }
    });
  }

  // ── Movies ── (the category id is also the fallback for opening a movie)
  const categories = (() => {
    const cached = cache.get(CacheKeys.movieCategories());
    if (cached?.pages) return cached;
    const provider = createInfiniteScroll(asList(moviesCategory("all")));
    cache.set(CacheKeys.movieCategories(), provider, CacheTTL.categories);
    return provider;
  })();

  createEffect(() => {
    const pages = categories.pages();
    const first = pages.find((cat: any) => (cat.category_id || cat.categoryID) !== "last_added");
    if (first) setMovieCategoryId(first.category_id || first.categoryID);
    // The categories call always adds "Last Added", so a first page with
    // nothing else in it means the real categories failed or are empty
    else if (pages.length > 0 || categories.end?.()) setIsLoadingMovies(false);
  });

  if (movies().length === 0) {
    const moviesData = createMemo(() => {
      const categoryId = movieCategoryId();
      if (!categoryId) return null;
      const cached = cache.get(CacheKeys.movies(categoryId));
      if (cached?.pages) return cached;
      const provider = createInfiniteScroll(asList(moviesProvider("all", categoryId)));
      cache.set(CacheKeys.movies(categoryId), provider, CacheTTL.movies);
      return provider;
    });

    createEffect(() => {
      const provider = moviesData();
      if (!provider || movies().length > 0) return;
      const pages = provider.pages();
      if (pages.length > 0) {
        // Fisher-Yates shuffle, take the first 10
        const available = [...pages];
        const count = Math.min(10, available.length);
        for (let i = 0; i < count; i++) {
          const j = Math.floor(Math.random() * (available.length - i)) + i;
          [available[i], available[j]] = [available[j], available[i]];
        }
        const selected = available.slice(0, count);
        cache.set(CacheKeys.homeMovies(), selected, CacheTTL.homeMovies);
        setMovies(selected);
        setIsLoadingMovies(false);
      } else if (provider.end()) {
        setIsLoadingMovies(false);
      }
    });
  }

  // ── Services ── same request as the Services panel
  const cachedServices = cache.get(SERVICES_CACHE_KEY);
  const [services, setServices] = createSignal<any[]>(Array.isArray(cachedServices) ? cachedServices : []);
  const [isLoadingServices, setIsLoadingServices] = createSignal(services().length === 0);

  if (services().length === 0) {
    const url = `${playListUrl()}/player_api.php?username=${playListUsername()}&password=${playListPassword()}&action=get_services`;
    asList(() => fetch(url).then((r) => r.json()))(1).then((list) => {
      if (list.length > 0) cache.set(SERVICES_CACHE_KEY, list, SERVICES_TTL);
      setServices(list);
      setIsLoadingServices(false);
    });
  }

  return {
    channels, movies, services,
    isLoadingChannels, isLoadingMovies, isLoadingServices,
    movieCategoryId,
  };
}
